'use client';
import React, { useState } from 'react';
import { MenuNavigation as SharedMenuNavigation } from '@launchstore/shared-puck';
import type { MenuNavigationProps, SharedNavMenuItem } from '@launchstore/shared-puck';
import type { EnrichedMenuItem } from '@/lib/menu/types';

/**
 * Storefront wrapper: pulls the active menu items via the layout's
 * data-injection pattern (already done in app/[countryCode]/(main)/layout.tsx).
 * For Puck-canvas usage in storefront, the layout injects menuData
 * via a server-side fetch.
 */
const enrichUrl = (item: any): string => {
  if (item.url) return item.url;
  if (item.type === 'category' && item.entityId) return `/categories/${item.entityId}`;
  if (item.type === 'collection' && item.entityId) return `/collections/${item.entityId}`;
  if (item.type === 'page' && item.entityId) return `/pages/${item.entityId}`;
  return '#';
};

const projectFromEnriched = (item: EnrichedMenuItem, depth: number = 0): SharedNavMenuItem => ({
  id: item.id,
  label: item.label,
  url: item.enrichedData?.resolvedUrl || enrichUrl(item),
  position: item.position,
  isVisible: item.isVisible,
  openInNewTab: item.openInNewTab,
  children: (item.children || []).filter((c) => c.isVisible).map((c) => projectFromEnriched(c, depth + 1)),
  megaMenu: item.megaMenu ? {
    enabled: !!item.megaMenu.enabled,
    columns: item.megaMenu.columns,
    showImage: item.megaMenu.showImage,
    imageUrl: item.megaMenu.imageUrl,
  } : undefined,
});

const projectFromApi = (item: any, depth: number = 0): SharedNavMenuItem => ({
  id: item.id,
  label: item.label,
  url: enrichUrl(item),
  position: item.position || 0,
  isVisible: item.isVisible !== false,
  openInNewTab: !!item.openInNewTab,
  children: (item.children || []).map((c: any) => projectFromApi(c, depth + 1)),
  megaMenu: item.megaMenu ? {
    enabled: !!item.megaMenu.enabled,
    columns: item.megaMenu.columns,
    showImage: item.megaMenu.showImage,
    imageUrl: item.megaMenu.imageUrl,
  } : undefined,
});

export interface StorefrontMenuNavigationProps extends MenuNavigationProps {
  /** Already-enriched menu data (preferred path) */
  enrichedItems?: EnrichedMenuItem[];
}

/**
 * Storefront wrapper for MenuNavigation. Supports two data
 * injection paths:
 *
 * 1. `enrichedItems` — already-enriched data from Medusa. Takes
 *    priority over `menuData`.
 * 2. `menuData` — raw API menu data (used by Puck canvas preview).
 */
const SharedMenuNavigationRender = (SharedMenuNavigation as any).render;

const MenuNavigationImpl: React.FC<StorefrontMenuNavigationProps> = (props: any) => {
  const { enrichedItems, menuData } = props;

  let projected: SharedNavMenuItem[] = [];
  if (enrichedItems && enrichedItems.length > 0) {
    projected = enrichedItems.map((it) => projectFromEnriched(it));
  } else if (menuData && menuData.length > 0) {
    projected = menuData.map((it: any) => projectFromApi(it));
  }

  return <SharedMenuNavigationRender {...(props as any)} menuData={projected} />;
};

export const MenuNavigation: any = { ...SharedMenuNavigation, render: (props: any) => <MenuNavigationImpl {...props} /> };
export default MenuNavigation;
export type { StorefrontMenuNavigationProps };
